import type { FetchTrackedOneGraphOperation } from "./ApolloNativeModule";
import type { FragmentRegistryAPI } from "@apollo/client/cache";

import { createOneGraphApolloClient } from "./createOneGraphApolloClient";

import {
  type ApolloCache,
  type ApolloClient,
  type ApolloLink,
  ApolloProvider,
  type DefaultOptions,
  type NormalizedCacheObject,
  type TypePolicies,
} from "@apollo/client";
import { useMemo } from "react";

export interface PlatformOptions {
  web?: {
    graphqlUrl?: string;
    graphqlApiKey?: string;
  };
}

export interface Props {
  children?: React.ReactNode;
  fetch: FetchTrackedOneGraphOperation;
  rootTrackingKey?: string;
  defaultOptions?: DefaultOptions;
  cache?: ApolloCache<NormalizedCacheObject>;
  platformOptions?: PlatformOptions;
  typePolicies?: TypePolicies;
  errorLink?: ApolloLink;
  prforceGroups?: string;
  fragments?: FragmentRegistryAPI;
  placementId?: string;
  /**
   * Externally-created client to use instead of creating one internally.
   * When provided (e.g. an SSR client supplied by the render entry), the
   * provider skips creating its own client.
   */
  externalClient?: ApolloClient<NormalizedCacheObject>;
  country?: string;
}

export function OneGraphApolloClientProvider(props: Props): React.ReactNode {
  const {
    children,
    fetch,
    rootTrackingKey,
    defaultOptions,
    cache,
    platformOptions,
    typePolicies,
    errorLink,
    prforceGroups,
    fragments,
    placementId,
    externalClient,
    country,
  } = props;

  const createdClient = useMemo(() => {
    if (externalClient) return null;
    return createOneGraphApolloClient({
      fetch,
      trackingKey: rootTrackingKey,
      defaultOptions,
      cache,
      platformOptions,
      typePolicies,
      errorLink,
      prforceGroups,
      fragments,
      placementId,
      country,
    });
  }, [
    externalClient,
    rootTrackingKey,
    defaultOptions,
    cache,
    platformOptions,
    fetch,
    typePolicies,
    errorLink,
    prforceGroups,
    fragments,
    placementId,
    country,
  ]);

  const client = externalClient ?? createdClient!;
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
